import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import * as Headless from '@headlessui/react';
import { Link } from './link';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        accent: 'bg-accent text-accent-foreground hover:bg-accent/90',
        muted:
          'bg-muted text-foreground hover:bg-muted/80 dark:text-muted-foreground dark:hover:text-foreground',
        ghost:
          'text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline hover:text-primary/80',
        plain:
          'bg-transparent text-foreground hover:bg-accent/10 hover:text-accent dark:hover:text-accent-foreground',
        crimson:
          'bg-flame text-white hover:bg-flame/90 dark:bg-flame dark:text-white',
        mint: 'bg-sunlight text-black hover:bg-sunlight/90 dark:bg-sunlight dark:text-black',
        blue: 'bg-ocean text-white hover:bg-ocean/90 dark:bg-ocean dark:text-white',
        black:
          'bg-black text-white hover:bg-black/90 dark:bg-black dark:text-white',
        subtle: '',
        underline: '',
        nav: '',
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/90',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  href?: string;
  className?: string;
}

export const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(function Button(
  { variant, size, className, children, ...props }: ButtonProps,
  ref
) {
  const classes = cn(buttonVariants({ variant, size }), className);

  return 'href' in props ? (
    <Link
      useOwnVariants={false}
      {...(props as any)}
      className={classes}
      ref={ref as React.ForwardedRef<HTMLAnchorElement>}
    >
      <TouchTarget>{children}</TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...props} className={classes} ref={ref}>
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  );
});

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  );
}

export { buttonVariants };
